import { useState, useEffect } from "react";
import { productService } from "../../services/product.service";
import type { Batch, Product } from "../../services/product.service";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { showToast } from "../../lib/toast";

export function BatchesPage() {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBatch, setEditingBatch] = useState<Batch | null>(null);
  const [filterProductId, setFilterProductId] = useState<string>("");
  const [showExpiring, setShowExpiring] = useState(false);
  const [showExpired, setShowExpired] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    batch: Batch | null;
  }>({
    isOpen: false,
    batch: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const [formData, setFormData] = useState({
    productId: "",
    batchNumber: "",
    expiryDate: "",
    quantity: "",
    costPrice: "",
    mrp: "",
  });

  useEffect(() => {
    loadData();
  }, [filterProductId, showExpiring, showExpired]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load products for dropdown
      const productsData = await productService.getProducts({
        page: 1,
        limit: 1000,
      });
      setProducts(productsData.data);

      // Load batches based on filters
      let batchesData: Batch[];
      if (showExpiring) {
        batchesData = await productService.getExpiringBatches(30);
      } else if (showExpired) {
        batchesData = await productService.getExpiredBatches();
      } else if (filterProductId) {
        batchesData = await productService.getBatches({
          productId: filterProductId,
        });
      } else {
        batchesData = await productService.getBatches();
      }

      setBatches(batchesData);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load batches");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (batch: Batch) => {
    setEditingBatch(batch);
    setFormData({
      productId: batch.productId,
      batchNumber: batch.batchNumber,
      expiryDate: new Date(batch.expiryDate).toISOString().split("T")[0],
      quantity: batch.quantity.toString(),
      costPrice: Number(batch.costPrice).toString(),
      mrp: Number(batch.mrp).toString(),
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingBatch(null);
    setFormData({
      productId: "",
      batchNumber: "",
      expiryDate: "",
      quantity: "",
      costPrice: "",
      mrp: "",
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingBatch) return;
    try {
      setError(null);
      await productService.updateBatch(editingBatch.id, {
        batchNumber: formData.batchNumber,
        expiryDate: formData.expiryDate,
        costPrice: parseFloat(formData.costPrice),
        mrp: parseFloat(formData.mrp),
      });

      handleCloseModal();
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to save batch");
    }
  };

  const handleDeleteClick = (batch: Batch) => {
    if (batch.quantity > 0) {
      showToast.error(
        "Cannot delete batch with remaining stock. Adjust stock to zero first.",
      );
      return;
    }
    setDeleteModal({ isOpen: true, batch });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.batch) return;
    setIsDeleting(true);
    try {
      setError(null);
      await productService.deleteBatch(deleteModal.batch.id);
      showToast.success("Batch deleted successfully");
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to delete batch");
    } finally {
      setIsDeleting(false);
      setDeleteModal({ isOpen: false, batch: null });
    }
  };

  const getExpiryStatus = (expiryDate: string) => {
    const now = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.floor(
      (expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (daysUntilExpiry < 0) {
      return { label: "EXPIRED", className: "bg-red-100 text-red-800" };
    } else if (daysUntilExpiry <= 30) {
      return {
        label: `${daysUntilExpiry}d left`,
        className: "bg-orange-100 text-orange-800",
      };
    } else if (daysUntilExpiry <= 90) {
      return {
        label: `${daysUntilExpiry}d left`,
        className: "bg-yellow-100 text-yellow-800",
      };
    } else {
      return {
        label: `${daysUntilExpiry}d left`,
        className: "bg-green-100 text-green-800",
      };
    }
  };

  const getProductName = (productId: string) => {
    const product = products.find((p) => p.id === productId);
    return product ? product.name : "Unknown";
  };

  const getTotalStockValue = () => {
    return batches.reduce((sum, batch) => sum + batch.quantity * batch.mrp, 0);
  };

  if (loading && batches.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading batches...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Batch Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            Track inventory batches and expiry dates
          </p>
        </div>
        <p className="text-sm text-gray-500 max-w-md text-right">
          New lots are created by receiving or import commands.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="text-sm text-gray-500">Total Batches</div>
          <div className="text-2xl font-bold text-gray-900">
            {batches.length}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="text-sm text-gray-500">Total Stock</div>
          <div className="text-2xl font-bold text-gray-900">
            {batches.reduce((sum, b) => sum + b.quantity, 0)} units
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="text-sm text-gray-500">Stock Value</div>
          <div className="text-2xl font-bold text-gray-900">
            ₹{getTotalStockValue().toFixed(2)}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="text-sm text-gray-500">Products</div>
          <div className="text-2xl font-bold text-gray-900">
            {products.length}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Product
            </label>
            <select
              value={filterProductId}
              onChange={(e) => setFilterProductId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Products</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => {
                setShowExpiring(!showExpiring);
                setShowExpired(false);
              }}
              className={`px-4 py-2 rounded-lg transition-colors ${
                showExpiring
                  ? "bg-orange-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Expiring Soon (30d)
            </button>
            <button
              onClick={() => {
                setShowExpired(!showExpired);
                setShowExpiring(false);
              }}
              className={`px-4 py-2 rounded-lg transition-colors ${
                showExpired
                  ? "bg-red-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Expired
            </button>
            <button
              onClick={() => {
                setShowExpiring(false);
                setShowExpired(false);
                setFilterProductId("");
              }}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Batches Table */}
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Product
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Batch Number
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Expiry Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Quantity
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cost Price
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                MRP
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {batches.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-6 py-12 text-center text-gray-500"
                >
                  No inventory lots found.
                </td>
              </tr>
            ) : (
              batches.map((batch) => {
                const expiryStatus = getExpiryStatus(batch.expiryDate);
                return (
                  <tr key={batch.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {getProductName(batch.productId)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {batch.batchNumber}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(batch.expiryDate).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${expiryStatus.className}`}
                      >
                        {expiryStatus.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm text-gray-900">
                        {batch.quantity}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm text-gray-900">
                        ₹{Number(batch.costPrice).toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm text-gray-900">
                        ₹{Number(batch.mrp).toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleOpenModal(batch)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteClick(batch)}
                        className="text-red-600 hover:text-red-900"
                        disabled={batch.quantity > 0}
                        title={
                          batch.quantity > 0
                            ? "Cannot delete batch with stock. Adjust to zero first."
                            : "Delete batch"
                        }
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Canonical lot metadata correction modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  Correct Lot Metadata
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Product Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.productId}
                    onChange={(e) =>
                      setFormData({ ...formData, productId: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled
                  >
                    <option value="">Select a product</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name} - {product.sku}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Product cannot be changed
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Batch Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Batch Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.batchNumber}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          batchNumber: e.target.value,
                        })
                      }
                      placeholder="BAT-001"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Expiry Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expiry Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.expiryDate}
                      onChange={(e) =>
                        setFormData({ ...formData, expiryDate: e.target.value })
                      }
                      min={new Date().toISOString().split("T")[0]}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Quantity is canonical inventory state, never a batch writer */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantity
                    </label>
                    <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600">
                      {editingBatch?.quantity ?? 0} units available
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Inventory quantities are controlled by the canonical stock
                      ledger.
                    </p>
                  </div>

                  {/* Cost Price */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cost Price <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.01"
                      value={formData.costPrice}
                      onChange={(e) =>
                        setFormData({ ...formData, costPrice: e.target.value })
                      }
                      placeholder="50.00"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* MRP */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      MRP <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.01"
                      value={formData.mrp}
                      onChange={(e) =>
                        setFormData({ ...formData, mrp: e.target.value })
                      }
                      placeholder="75.00"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Margin Display */}
                  {formData.costPrice && formData.mrp && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Margin
                      </label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm font-semibold">
                        {(
                          ((Number(formData.mrp) - Number(formData.costPrice)) /
                            Number(formData.costPrice)) *
                          100
                        ).toFixed(2)}
                        %
                      </div>
                    </div>
                  )}
                </div>

                {/* Form Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Apply Correction
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, batch: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Batch"
        message={`Are you sure you want to delete batch "${deleteModal.batch?.batchNumber}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}
